import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchUsers,
  approveUser,
  setUserRole,
  deactivateUser,
  reactivateUser,
} from '../../lib/admin.js';
import Paginator from '../common/Paginator.jsx';

const PAGE_SIZE = 20;
// Pending requests surface first; within each group the fetch order
// (newest first) is preserved by the stable sort.
const STATUS_ORDER = { pending_approval: 0, active: 1, deactivated: 2 };

export default function UsersList({ currentUserId }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [page, setPage] = useState(0);

  async function load() {
    try {
      setError(null);
      const rows = await fetchUsers();
      setUsers(rows);
    } catch (err) {
      setError(err.message ?? String(err));
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function run(fn, id) {
    setBusyId(id);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err.message ?? String(err));
    } finally {
      setBusyId(null);
    }
  }

  const sorted = useMemo(
    () =>
      users
        ? [...users].sort(
            (a, b) => (STATUS_ORDER[a.status] ?? 3) - (STATUS_ORDER[b.status] ?? 3)
          )
        : null,
    [users]
  );

  const counts = useMemo(() => {
    const acc = {};
    for (const u of users ?? []) acc[u.status] = (acc[u.status] ?? 0) + 1;
    return acc;
  }, [users]);

  if (error) {
    return (
      <p className="rounded-lg border border-red-500/40 bg-red-950/30 p-6 text-red-200">{error}</p>
    );
  }

  if (!users) {
    return (
      <p className="rounded-lg border border-white/10 bg-navy-base/30 p-8 text-center text-ink-secondary">
        {t('common.loading')}
      </p>
    );
  }

  if (users.length === 0) {
    return (
      <p className="rounded-lg border border-white/10 bg-navy-base/30 p-8 text-center text-ink-secondary">
        {t('admin.users.empty')}
      </p>
    );
  }

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  // Clamp instead of resetting so approving a user mid-list doesn't yank
  // the admin back to page 1, but a shrinking list can't strand them past
  // the end either.
  const safePage = Math.min(page, totalPages - 1);
  const pageItems = sorted.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="rounded-full border border-gold/40 bg-navy-mid/40 px-3 py-1 text-sm text-gold-light">
          {t('admin.users.total', { count: users.length })}
        </span>
        {['pending_approval', 'active', 'deactivated'].map((s) =>
          counts[s] ? (
            <Badge
              key={s}
              tone={s === 'active' ? 'positive' : s === 'pending_approval' ? 'pending' : 'muted'}
            >
              {t(`profile.statusLabel.${s}`)}: {counts[s]}
            </Badge>
          ) : null
        )}
      </div>
      <div className="overflow-hidden rounded-lg border border-white/10 bg-navy-base/40">
      <table className="w-full text-sm">
        <thead className="bg-navy-mid/40 text-[0.7rem] uppercase tracking-[0.12em] text-ink-muted">
          <tr>
            <th className="px-4 py-3 text-left">{t('admin.users.email')}</th>
            <th className="px-4 py-3 text-left">{t('admin.users.role')}</th>
            <th className="px-4 py-3 text-left">{t('admin.users.status')}</th>
            <th className="px-4 py-3 text-right">{t('admin.users.actions')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {pageItems.map((u) => {
            const isMe = u.id === currentUserId;
            const busy = busyId === u.id;
            return (
              <tr key={u.id} className="text-cream">
                <td className="px-4 py-3">
                  <div>{u.display_name || u.email}</div>
                  {u.display_name && (
                    <div className="text-xs text-ink-muted">{u.email}</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <Badge tone={u.role === 'admin' ? 'admin' : 'default'}>
                    {t(`profile.roleLabel.${u.role}`)}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <Badge tone={u.status === 'active' ? 'positive' : u.status === 'pending_approval' ? 'pending' : 'muted'}>
                    {t(`profile.statusLabel.${u.status}`)}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex flex-wrap justify-end gap-2">
                    {u.status === 'pending_approval' && (
                      <button
                        type="button"
                        className="rounded-md border border-emerald-500/40 px-3 py-1 text-xs text-emerald-200 hover:border-emerald-400"
                        disabled={busy}
                        onClick={() => run(() => approveUser(u.id), u.id)}
                      >
                        {t('admin.users.approve')}
                      </button>
                    )}
                    {!isMe && u.role !== 'admin' && u.status === 'active' && (
                      <button
                        type="button"
                        className="rounded-md border border-blue-accent/50 px-3 py-1 text-xs text-blue-glow hover:border-blue-accent"
                        disabled={busy}
                        onClick={() => run(() => setUserRole(u.id, 'admin'), u.id)}
                      >
                        {t('admin.users.promote')}
                      </button>
                    )}
                    {!isMe && u.role === 'admin' && (
                      <button
                        type="button"
                        className="rounded-md border border-white/15 px-3 py-1 text-xs text-ink-secondary hover:border-gold/40"
                        disabled={busy}
                        onClick={() => run(() => setUserRole(u.id, 'collaborator'), u.id)}
                      >
                        {t('admin.users.demote')}
                      </button>
                    )}
                    {!isMe && u.status === 'deactivated' && (
                      <button
                        type="button"
                        className="rounded-md border border-emerald-500/40 px-3 py-1 text-xs text-emerald-200 hover:border-emerald-400"
                        disabled={busy}
                        onClick={() => run(() => reactivateUser(u.id), u.id)}
                      >
                        {t('admin.users.reactivate')}
                      </button>
                    )}
                    {!isMe && u.status !== 'deactivated' && (
                      <button
                        type="button"
                        className="rounded-md border border-red-500/40 px-3 py-1 text-xs text-red-200 hover:border-red-400"
                        disabled={busy}
                        onClick={() => run(() => deactivateUser(u.id), u.id)}
                      >
                        {t('admin.users.deactivate')}
                      </button>
                    )}
                    {isMe && <span className="text-xs text-ink-muted">{t('admin.users.you')}</span>}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      {totalPages > 1 && (
        <Paginator page={safePage} totalPages={totalPages} onPage={setPage} />
      )}
    </div>
  );
}

function Badge({ tone, children }) {
  const tones = {
    default: 'border-white/15 bg-navy-mid/40 text-ink-secondary',
    positive: 'border-emerald-500/40 bg-emerald-950/30 text-emerald-200',
    pending: 'border-gold/40 bg-navy-mid/40 text-gold-light',
    muted: 'border-white/10 bg-navy-deep/40 text-ink-muted',
    admin: 'border-blue-accent/50 bg-navy-mid/40 text-blue-glow',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[0.7rem] font-medium uppercase tracking-[0.12em] ${tones[tone]}`}
    >
      {children}
    </span>
  );
}
